import React, { useState } from "react";
import { ChefHat, LogOut, Plus, Search, X, Pin, Copy, Trash2, Folder, Star, Settings } from "lucide-react";
import { supabase } from "../supabaseClient";
import { STATUSES } from "../data/constants";
import CategoryAccordion from "./common/CategoryAccordion";

const BanquetList = ({
  banquets, setCurrentBanquet, setView, currentUser, logout, styles, t, setBanquets, companyLogo, halls
}) => {
  const [searchName, setSearchName] = useState("");
  const [searchHall, setSearchHall] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // --- ЛОГИКА ДУБЛИРОВАНИЯ ---
  const handleDuplicate = async (e, original) => {
    e.stopPropagation();
    if (!window.confirm(`${t.copy || "Copy"} "${original.name}"?`)) return;
    
    setIsLoading(true);
    try {
        const banquetCopy = {
            client_name: `${original.name} (${t.copy || "Copy"})`,
            event_date: original.date,
            start_time: original.startTime,
            event_type: original.eventType,
            hall_name: original.hall,
            guests_count: original.guests,
            prepayment: 0, 
            service_percent: original.servicePercent,
            status: 'draft',
            tech_equipment: original.techEquipment,
            category_notes: original.categoryNotes,
            is_pinned: false,
            price_per_person: original.pricePerPerson
        };

        const { data: newBanquetData, error: banquetError } = await supabase
            .from('banquets')
            .insert([banquetCopy])
            .select();

        if (banquetError) throw banquetError;
        const newBanquetID = newBanquetData[0].id;

        if (original.items && original.items.length > 0) {
            const itemsCopy = original.items.map(item => ({
                banquet_id: newBanquetID,
                dish_id: item.id,
                quantity: item.quantity,
                comment: item.comment || "",
                group_id: item.groupId,
                item_price: item.price,
                item_name: item.name
            }));

            const { error: itemsError } = await supabase.from('banquet_items').insert(itemsCopy);
            if (itemsError) throw itemsError;
        }

        const newBanquetForState = {
            ...original,
            id: newBanquetID,
            name: banquetCopy.client_name,
            status: 'draft',
            prepayment: 0,
            isPinned: false,
            items: original.items.map(i => ({ ...i, uid: Date.now() + Math.random() })) 
        };

        setBanquets([newBanquetForState, ...banquets]);

    } catch (error) {
        alert("Ошибка при копировании: " + error.message);
    } finally {
        setIsLoading(false);
    }
  };

  // --- ЛОГИКА ПИНА ---
  const togglePin = async (e, id, currentVal) => {
    e.stopPropagation();
    const newVal = !currentVal;
    setBanquets(banquets.map((b) => (b.id === id ? { ...b, isPinned: newVal } : b)));

    const { error } = await supabase.from('banquets').update({ is_pinned: newVal }).eq('id', id);
    
    if (error) {
        setBanquets(banquets.map((b) => (b.id === id ? { ...b, isPinned: currentVal } : b)));
    }
  };

  // --- ЛОГИКА УДАЛЕНИЯ ---
  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm(t.deleteConfirm)) return;

    const { error } = await supabase.from('banquets').delete().eq('id', id);

    if (error) {
        alert("Ошибка удаления: " + error.message);
    } else {
        setBanquets(banquets.filter((b) => b.id !== id));
    }
  };

  const resetFilters = () => { setSearchName(""); setSearchHall(""); };

  // --- ФИЛЬТРАЦИЯ ---
  const filteredBanquets = banquets.filter((b) => {
    const query = searchName.toLowerCase();
    const formattedDate = b.date ? b.date.split("-").reverse().join(".") : "";

    const matchText = 
        b.name.toLowerCase().includes(query) || 
        b.eventType.toLowerCase().includes(query) || 
        formattedDate.includes(query);

    const matchHall = searchHall ? b.hall === searchHall : true;

    return matchText && matchHall;
  });

  const pinnedBanquets = filteredBanquets.filter(b => b.isPinned);
  const unpinnedBanquets = filteredBanquets.filter(b => !b.isPinned);

  // --- ГРУППИРОВКА ---
  const grouped = unpinnedBanquets.reduce((acc, banquet) => {
    const date = banquet.date || "0000-00-00";
    const [year, month] = date.split("-");
    if (!acc[year]) acc[year] = {};
    if (!acc[year][month]) acc[year][month] = [];
    acc[year][month].push(banquet);
    return acc;
  }, {});

  const sortedYears = Object.keys(grouped).sort((a, b) => b - a); // От новых к старым

  // --- КОМПОНЕНТ КАРТОЧКИ ---
  const BanquetCard = ({ b }) => {
    const status = STATUSES.find((s) => s.label === b.status) || STATUSES[0];
    const formattedDate = b.date ? b.date.split("-").reverse().join(".") : "---";

    return (
      <div key={b.id} style={styles.list.banquetCard(status.color)} onClick={() => { setCurrentBanquet(b); setView("editor"); }}>
        <button onClick={(e) => togglePin(e, b.id, b.isPinned)} style={styles.list.pinButton(b.isPinned)}>
          <Pin size={18} fill={b.isPinned ? styles.colors.warning : "none"} />
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px", paddingRight: "25px" }}>
          <span style={{ padding: "4px 8px", borderRadius: "20px", fontSize: "11px", fontWeight: "bold", color: status.color, background: status.color + "20" }}>
            {t[status.label] || status.label}
          </span>
        </div>
        <h3 style={{ margin: "0 0 5px 0" }}>{b.eventType}: {b.name}</h3>
        <div style={{ fontSize: "12px", color: styles.colors.muted, display: "flex", gap: "15px", marginTop: "10px" }}>
          <span>📅 {formattedDate}</span>
          <span>📍 {b.hall}</span>
        </div>
        <div style={{ marginTop: "15px", borderTop: `1px solid ${styles.colors.inputBorder}`, paddingTop: "10px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button onClick={(e) => handleDuplicate(e, b)} disabled={isLoading} style={{ ...styles.btn, ...styles.btnSecondary, padding: "5px 10px", fontSize: "12px" }}><Copy size={14} /> {t.copy || "Copy"}</button>
          <button onClick={(e) => handleDelete(e, b.id)} style={{ ...styles.btn, ...styles.btnSecondary, padding: "5px 10px", fontSize: "12px", color: "#dc2626", borderColor: "#fee2e2" }}><Trash2 size={14} /></button>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
          <div style={{ width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {companyLogo ? <img src={companyLogo} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <ChefHat size={32} color="#3b82f6" />}
          </div>
          <div><h1 style={{ fontSize: "22px", margin: 0 }}>{t.appTitle}</h1><span style={{ fontSize: "12px", color: styles.colors.muted }}><b>{currentUser}</b></span></div>
        </div>
        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
          <button style={{ ...styles.btn, ...styles.btnSecondary, padding: "10px" }} onClick={logout}><LogOut size={16} /></button>
          <button style={{ ...styles.btn, backgroundColor: "#16a34a", color: "white", border: "none" }} onClick={() => setView("admin")}><Settings size={16} /> {t.manageDishes}</button>
          <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => { 
              const defaultHall = halls && halls.length > 0 ? halls[0].name : "";
              setCurrentBanquet({ status: "draft", name: "", eventType: "Свадьба", hall: defaultHall, date: new Date().toISOString().split("T")[0], guests: 20, servicePercent: 15, items: [], prepayment: 0, isPinned: false, startTime: "14:00" }); 
              setView("editor"); 
          }}>
            <Plus size={16} /> {t.newBanquet}
          </button>
        </div>
      </div>

      <div style={styles.list.searchContainer}>
        <div style={{ position: "relative", flex: 2, minWidth: "200px" }}>
          <Search size={16} style={{ position: "absolute", left: "10px", top: "12px", color: "#999" }} />
          <input style={{ ...styles.input, paddingLeft: "35px", margin: 0 }} placeholder={t.search} value={searchName} onChange={(e) => setSearchName(e.target.value)} />
        </div>
        <select style={{ ...styles.select, flex: 1, minWidth: "140px" }} value={searchHall} onChange={(e) => setSearchHall(e.target.value)}>
          <option value="">{t.allHalls || "All Halls"}</option>
          {halls && halls.map((h) => (<option key={h.id} value={h.name}>{h.name}</option>))}
        </select>
        {(searchName || searchHall) && <button style={{ ...styles.btn, ...styles.btnSecondary, width: "auto", padding: "10px" }} onClick={resetFilters}><X size={16} /></button>}
      </div>

      {/* ЗАКРЕПЛЕННЫЕ */}
      {pinnedBanquets.length > 0 && searchName.length === 0 && (
        <div style={{ marginBottom: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px", color: "#d97706" }}>
                <Star size={20} fill="#d97706" />
                <h2 style={{ margin: 0, fontSize: "18px" }}>{t.pinnedEvents}</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "15px" }}>
              {pinnedBanquets.map(b => <BanquetCard key={b.id} b={b} />)}
            </div>
        </div>
      )}

      {/* АРХИВ */}
      <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", color: styles.colors.muted }}>
            <Folder size={18} />
            <span style={{ fontSize: "14px", fontWeight: "bold" }}>{t.dateArchive}</span>
        </div>
        
        {sortedYears.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: styles.colors.muted }}>{t.emptyArchive}</div>
        ) : sortedYears.map((year) => (
            <div key={year} style={{ borderLeft: "4px solid #334155", paddingLeft: "10px", marginBottom: "10px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", color: styles.colors.text }}>
                <Folder size={20} fill="#334155" />
                <h2 style={{ margin: 0, fontSize: "18px" }}>{year === "0000" ? "---" : year}</h2>
              </div>
              
              {Object.keys(grouped[year]).sort((a, b) => b - a).map((month) => ( 
                <CategoryAccordion 
                    key={`${year}-${month}`} 
                    title={t.months[month]} // Здесь можно добавить t.months[month] если есть словарь месяцев
                    count={grouped[year][month].length} 
                    styles={styles} 
                    forceOpen={searchName.length > 0}
                > 
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "15px", padding: "15px" }}>
                    {grouped[year][month]
                        .sort((a, b) => new Date(b.date) - new Date(a.date)) // От новых к старым внутри месяца
                        .map((b) => <BanquetCard key={b.id} b={b} />)
                    }
                  </div>
                </CategoryAccordion>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
};

export default BanquetList;